import { api_configuration } from "../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_modifications = api_configuration.types.modifications.alan_api;
import { KeysOfType } from "./types";
import {
	ScalarProvider,
	ScalarConsumer,
	createExpressionBuilder,
	createStategroupExpressionBuilder,
	createDictionaryExpressionBuilder,
	DictionaryProvider,
	DictionaryConsumer,
	MutableVariable,
	InitializedDynamicDefaultExpressionBuilder,
	InitializedDynamicStategroupExpressionBuilder,
	InitializedStaticDictionaryExpressionBuilder,
	Scalar,
	WeakCache,
} from "../../../../stream";
import { OnDemandEvaluatedMap } from "../../../../destructables";
import { Pending } from "./Status";

class ScalarAlanPropertyVariable<
	N extends (api_cs.AlanNode | api_modifications.AlanNode) & { properties: Record<string, any> },
	T extends string | number
> extends ScalarProvider<T> {
	private readonly _subscribers = new Set<ScalarConsumer<T>>();
	private _subscription?: api_cs.Subscription;

	constructor(
		private alan_node: N,
		private property_name: KeysOfType<N["properties"], string | api_cs.AlanInteger | api_cs.Reference<any, any>>
	) {
		super();
	}

	protected receive(value: T): void {
		((this.alan_node as any)[`set_${String(this.property_name)}`] as (value: T) => void)(value);
	}

	set(value: T): void {
		this.receive(value);
	}

	get value() {
		const property: string | api_cs.AlanInteger | api_cs.Reference<any, any> =
			this.alan_node.properties[String(this.property_name)];
		if (property instanceof api_cs.AlanInteger) {
			return property.value as T;
		}
		if (property instanceof api_cs.Reference) {
			return property.entry as T;
		}
		return property as T;
	}

	subscribe(subscriber: ScalarConsumer<T>) {
		if (this.alan_node.destroyed) return subscriber;
		if (this._subscribers.has(subscriber)) throw new Error("unexpected");
		if (this._subscribers.size === 0) {
			this._subscription = (
				(this.alan_node as any)[`subscribe_${String(this.property_name)}`] as (
					observer: api_cs.TextObserver | api_cs.NumberObserver
				) => any
			)({
				on_update: () => {
					for (const subscriber of this._subscribers) {
						subscriber.update(this.value);
					}
				},
			});
		}
		this._subscribers.add(subscriber);
		subscriber.update(this.value);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<T>) {
		if ((<any>global)?.alan_strict && !this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.delete(subscriber);
		if (this._subscribers.size === 0) {
			this._subscription?.unsubscribe();
			this._subscription = undefined;
		}
	}
}

const scalarStreamCache = new OnDemandEvaluatedMap<
	api_cs.IAlanObject,
	OnDemandEvaluatedMap<string, ScalarAlanPropertyVariable<any, any>>
>((node) => {
	return new OnDemandEvaluatedMap<string, ScalarAlanPropertyVariable<any, any>>((property_name) => {
		return new ScalarAlanPropertyVariable(node as any, property_name);
	});
});
function getScalarStream<T extends string | number>(
	node: api_cs.IAlanObject,
	property_name: string
): ScalarAlanPropertyVariable<any, T> {
	return scalarStreamCache.get(node).get(property_name);
}

type StateOf<T> = T extends api_cs.StateGroup<infer STATE> ? STATE : never;
type StateType<State> = State extends { name: infer Tag; node: infer ValueType }
	? { tag: Tag; value: ValueType }
	: never;

class AlanStategroupVariable<T extends api_cs.StateGroup<any>> extends ScalarProvider<StateType<StateOf<T>>> {
	private readonly _subscribers = new Set<ScalarConsumer<StateType<StateOf<T>>>>();
	private _subscription?: api_cs.Subscription;

	constructor(private stategroup: T) {
		super();
	}

	get value() {
		const state = this.stategroup.state;
		return { tag: state.name, value: state.node } as StateType<StateOf<T>>;
	}

	subscribe(subscriber: ScalarConsumer<StateType<StateOf<T>>>) {
		if (this.stategroup.destroyed) return subscriber;
		if (this._subscribers.has(subscriber)) throw new Error("unexpected");
		if (this._subscribers.size === 0) {
			/* to remedy unsubscribing during first evaluation. Note that this is exceptional and an antipattern */
			this._subscription = this.stategroup.subscribe({
				on_update: () => {
					for (const subscriber of this._subscribers) {
						subscriber.update(this.value);
					}
				},
			});
		}
		this._subscribers.add(subscriber);
		subscriber.update(this.value);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<StateType<StateOf<T>>>) {
		if ((<any>global)?.alan_strict && !this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.delete(subscriber);
		if (this._subscribers.size === 0) {
			this._subscription?.unsubscribe();
			this._subscription = undefined;
		}
	}
}

const getStategroupStream = WeakCache<api_cs.StateGroup<any>, AlanStategroupVariable<any>>(
	(stategroup) => new AlanStategroupVariable(stategroup)
);

class AlanDictionaryVariable<E extends api_cs.AlanDictionaryEntry, I> extends DictionaryProvider<string, E> {
	private readonly _subscribers = new Set<DictionaryConsumer<string, E>>();
	private _subscription?: api_cs.Subscription;
	private _values = new Map<string, E>();

	constructor(private _dictionary: api_cs.AlanDictionary<{ node: E; init: I }, any>) {
		super();
	}

	add(key: string, value: E) {
		if (this._values.has(key)) return;
		this._values.set(key, value);
		for (const subscriber of this._subscribers) {
			subscriber.add(key, value);
		}
		this._size?.ensure(this._dictionary.size);
	}

	remove(key: string) {
		this._values.delete(key);
		for (const subscriber of this._subscribers) {
			subscriber.remove(key);
		}
		this._size?.ensure(this._dictionary.size);
	}

	subscribe(subscriber: DictionaryConsumer<string, E>) {
		if (this._dictionary.destroyed) throw new Error("unexpected");
		if (this._subscribers.has(subscriber)) throw new Error("unexpected");
		if (this._subscribers.size === 0) {
			this._subscription = this._dictionary.subscribe({
				on_add: (entry) => this.add(entry.key_value, entry),
				on_remove: (key) => this.remove(key),
			});
			for (const entry of this._dictionary) this.add(...entry);
		}
		this._subscribers.add(subscriber);
		for (const entry of this._values) subscriber.add(...entry);
		return subscriber;
	}

	unsubscribe(subscriber: DictionaryConsumer<string, E>) {
		if ((<any>global)?.alan_strict && !this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.delete(subscriber);
		if (this._subscribers.size === 0) {
			this._subscription?.unsubscribe();
			this._subscription = undefined;
			this._values.clear();
		}
	}

	get entries(): ReadonlyMap<string, E> {
		return new Map([...this._dictionary.entries()]);
	}

	private _size: MutableVariable<number> | undefined;
	get size(): ScalarProvider<number> {
		return (this._size ??= new MutableVariable(this._dictionary.size));
	}
}

const getDictionaryStream = WeakCache<api_cs.AlanDictionary<any, any>, AlanDictionaryVariable<any, any>>(
	(dictionary) => new AlanDictionaryVariable(dictionary)
);

export function toVariable<
	N extends (api_cs.AlanNode | api_modifications.AlanNode) & { properties: Record<string, any> }
>(
	node: N,
	text_property_name: KeysOfType<N["properties"], string | api_cs.Reference<any, any>>
): ScalarProvider<string>;

export function toVariable<
	N extends (api_cs.AlanNode | api_modifications.AlanNode) & { properties: Record<string, any> }
>(node: N, number_property_name: KeysOfType<N["properties"], api_cs.AlanInteger>): ScalarProvider<number>;

export function toVariable<T extends api_cs.StateGroup<any>>(stategroup: T): ScalarProvider<StateType<StateOf<T>>>;

export function toVariable<E extends api_cs.AlanDictionaryEntry>(
	dictionary: api_cs.AlanDictionary<{ node: E; init: any }, any>
): DictionaryProvider<string, E>;

export function toVariable(alan_object: api_cs.IAlanObject, property_name?: string): Scalar<any> {
	if (alan_object instanceof api_cs.AlanDictionary) {
		return getDictionaryStream(alan_object);
	}
	if (alan_object instanceof api_cs.StateGroup) {
		return getStategroupStream(alan_object);
	}
	return getScalarStream(alan_object as any, property_name!);
}

export function toStream<
	N extends (api_cs.AlanNode | api_modifications.AlanNode) & { properties: Record<string, any> }
>(
	node: N,
	text_property_name: KeysOfType<N["properties"], string | api_cs.Reference<any, any>>
): InitializedDynamicDefaultExpressionBuilder<void, string>;

export function toStream<
	N extends (api_cs.AlanNode | api_modifications.AlanNode) & { properties: Record<string, any> }
>(
	node: N,
	number_property_name: KeysOfType<N["properties"], api_cs.AlanInteger>
): InitializedDynamicDefaultExpressionBuilder<void, number>;

export function toStream<T extends api_cs.StateGroup<{ name: string; node: any; init: any }>>(
	stategroup: T
): InitializedDynamicStategroupExpressionBuilder<void, StateType<StateOf<T>>>;

export function toStream<E extends api_cs.AlanDictionaryEntry>(
	dictionary: api_cs.AlanDictionary<{ node: E; init: any }, any>
): InitializedStaticDictionaryExpressionBuilder<void, string, E>;

export function toStream(alan_object: api_cs.IAlanObject, property_name?: string): () => Scalar<any> {
	if (alan_object instanceof api_cs.AlanDictionary) {
		return createDictionaryExpressionBuilder(getDictionaryStream(alan_object));
	}
	if (alan_object instanceof api_cs.StateGroup) {
		return createStategroupExpressionBuilder(getStategroupStream(alan_object));
	}
	return createExpressionBuilder(getScalarStream(alan_object as any, property_name!));
}

export class PromiseSynchronizer<Type> extends ScalarProvider<Type | Pending> implements ScalarConsumer<Promise<Type>> {
	private _subscriber: ScalarConsumer<Type | Pending> | undefined;
	private _value: Type | Pending | undefined;

	constructor(readonly source: ScalarProvider<Promise<Type>>, readonly __origin?: string) {
		super();
	}

	get value() {
		if (this._subscriber === undefined) {
			return Pending;
		} else {
			return this._value!;
		}
	}

	update(promise: Promise<Type>) {
		if (this._value === undefined) {
			this._value = Pending;
			this._subscriber!.update(this._value);
		}
		promise.then((value) => {
			this._value = value;
			this._subscriber?.update(this._value);
		});
	}

	subscribe(subscriber: ScalarConsumer<Type>): typeof subscriber {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		this.source.subscribe(this);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<Type>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		this.source.unsubscribe(this);
		this._value = undefined;
	}
}

export function synchronize<T>(source: ScalarProvider<Promise<T>>): ScalarProvider<T | Pending> {
	return new PromiseSynchronizer(source);
}
